import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '@/redux/common/baseQuery';

// ── Types ──────────────────────────────────────────────────────────────────

export interface NotificationRecipient {
  user_id?: string;
  to_email?: string;
  to_phone?: string;
  device_tokens?: string[];
}

export interface NotificationRequest {
  id: string;
  tenant_id?: string;
  notification_code?: string;
  classification?: string;
  is_system_critical: boolean;
  source_module?: string;
  source_entity_type?: string;
  source_entity_id?: string;
  triggered_by_user_id?: string;
  recipient?: NotificationRecipient;
  delivery_mode?: string;
  resolved_channels: string[];
  status?: string;
  queue_status?: string;
  queue_attempts: number;
  queue_error?: string;
  attempts_count: number;
  created_on?: string;
  sent_on?: string;
  last_error?: string;
  delivery_logs?: DeliveryLog[];
}

export interface RequestSummaryCounts {
  total: number;
  delivered: number;
  dispatch_failed: number;
  queue_failed: number;
}

export interface DeliveryLog {
  id: string;
  channel?: string;
  recipient_address?: string;
  delivery_status?: string;
  attempt_no: number;
  provider?: string;
  provider_reference_no?: string;
  failure_reason?: string;
  failure_category?: string;
  sent_on?: string;
  created_on?: string;
}

export interface NotificationDirectoryEntry {
  id: string;
  notification_code?: string;
  description?: string;
  classification?: string;
  default_classification?: string;
  is_system_critical: boolean;
  tenant_override_allowed: boolean;
  mandatory_channels: string[];
  optional_channels: string[];
  default_locale?: string;
  is_active: boolean;
}

export interface NotificationTemplate {
  id: string;
  notification_code?: string;
  channel?: string;
  language?: string;
  subject_template?: string;
  body_template?: string;
  required_placeholders: string[];
  version: number;
  is_default: boolean;
  is_active: boolean;
  tenant_id?: string;
}

export interface NotificationPreference {
  id: string;
  user_id?: string;
  tenant_id?: string;
  email_enabled: boolean;
  sms_enabled: boolean;
  push_enabled: boolean;
  in_app_enabled: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  muted_classifications: string[];
  updated_on?: string;
}

export interface ListResponse<T> {
  success: boolean;
  message_code: number;
  data: T[];
  total: number;
}

export interface SingleResponse<T> {
  success: boolean;
  message_code: number;
  data: T;
}

// ── API ────────────────────────────────────────────────────────────────────

export const notificationsAdminApi = createApi({
  reducerPath: 'notificationsAdminApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['Request', 'Template', 'Directory', 'Preference'],
  endpoints: (builder) => ({

    // Requests
    getRequestsSummary: builder.query<SingleResponse<RequestSummaryCounts>, void>({
      query: () => '/api/v1/notifications/admin/requests/summary',
      providesTags: ['Request'],
    }),

    listRequests: builder.query<ListResponse<NotificationRequest>, {
      status?: string;
      queue_status?: string;
      notification_code?: string;
      source_module?: string;
      tenant_id?: string;
      recipient_search?: string;
      from_date?: string;
      to_date?: string;
      limit?: number;
      skip?: number;
    }>({
      query: (params) => ({ url: '/api/v1/notifications/admin/requests', params }),
      providesTags: ['Request'],
    }),

    getRequest: builder.query<SingleResponse<NotificationRequest>, string>({
      query: (id) => `/api/v1/notifications/admin/requests/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'Request', id }],
    }),

    retryRequest: builder.mutation<SingleResponse<{ queued_channels: number }>, string>({
      query: (id) => ({ url: `/api/v1/notifications/admin/requests/${id}/retry`, method: 'POST' }),
      invalidatesTags: ['Request'],
    }),

    cancelRequest: builder.mutation<{ success: boolean; message_code: number }, string>({
      query: (id) => ({ url: `/api/v1/notifications/admin/requests/${id}/cancel`, method: 'POST' }),
      invalidatesTags: ['Request'],
    }),

    discardRequest: builder.mutation<{ success: boolean; message_code: number }, string>({
      query: (id) => ({ url: `/api/v1/notifications/admin/requests/${id}/discard`, method: 'POST' }),
      invalidatesTags: ['Request'],
    }),

    // Delivery logs
    listDeliveryLogs: builder.query<ListResponse<DeliveryLog>, {
      channel?: string;
      delivery_status?: string;
      from_date?: string;
      to_date?: string;
      limit?: number;
      skip?: number;
    }>({
      query: (params) => ({ url: '/api/v1/notifications/admin/delivery-logs', params }),
    }),

    // Directory
    listDirectory: builder.query<ListResponse<NotificationDirectoryEntry>, {
      search?: string;
      channel?: string;
      classification?: string;
    }>({
      query: (params) => ({ url: '/api/v1/notifications/admin/directory', params }),
      providesTags: ['Directory'],
    }),

    updateDirectoryEntry: builder.mutation<SingleResponse<NotificationDirectoryEntry>, {
      notification_code: string;
      body: {
        default_classification?: string | null;
        mandatory_channels?: string[];
        optional_channels?: string[];
        tenant_override_allowed?: boolean;
        is_active?: boolean;
      };
    }>({
      query: ({ notification_code, body }) => ({
        url: `/api/v1/notifications/admin/directory/${notification_code}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Directory'],
    }),

    // Templates
    listTemplates: builder.query<ListResponse<NotificationTemplate>, {
      notification_code?: string;
      channel?: string;
      language?: string;
    }>({
      query: (params) => ({ url: '/api/v1/notifications/admin/templates', params }),
      providesTags: ['Template'],
    }),

    updateTemplate: builder.mutation<{ success: boolean; message_code: number }, { id: string; body: Partial<NotificationTemplate> }>({
      query: ({ id, body }) => ({ url: `/api/v1/notifications/admin/templates/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Template'],
    }),

    // Preferences
    listPreferences: builder.query<ListResponse<NotificationPreference>, {
      search?: string;
      limit?: number;
      skip?: number;
    }>({
      query: (params) => ({ url: '/api/v1/notifications/admin/preferences', params }),
      providesTags: ['Preference'],
    }),
  }),
});

export const {
  useGetRequestsSummaryQuery,
  useListRequestsQuery,
  useGetRequestQuery,
  useRetryRequestMutation,
  useCancelRequestMutation,
  useDiscardRequestMutation,
  useListDeliveryLogsQuery,
  useListDirectoryQuery,
  useUpdateDirectoryEntryMutation,
  useListTemplatesQuery,
  useUpdateTemplateMutation,
  useListPreferencesQuery,
} = notificationsAdminApi;
